/** @format */

import { alpha } from "@mui/material/styles";
import { Avatar, Box, Stack, TableCell, TableRow, Tooltip, Typography } from "@mui/material";
import React, { useState } from "react";
import PropTypes from "prop-types";
import { colors, fonts } from "../../../utility/types/constant";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../redux/store/store";
import { SendAddRequestButton } from "../../contribute-btn/styles";
import {
	selectIsLoading,
	selectedCurrentCircleInfo,
} from "../../../redux/selectors/circleSelector";
import { fetchLeaderSendAddRequest } from "../../../redux/actions/circleAction";
import { unwrapResult } from "@reduxjs/toolkit";
import LoadingSpinner from "../../loading-spinner/LoadingSpinner";
import { useParams } from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material/styles";

interface SearchUserResultTableProps {
	requestId: string;
	profileImage: string;
	fullName: string;
	email: string;
	phone: string;
	selected?: any;
	handleOpenNotificationModal: any;
	message?: string;
	hasPendingAddRequest: boolean;
}

const SearchUserResultTable = (props: SearchUserResultTableProps) => {
	const {
		requestId,
		profileImage,
		fullName,
		email,
		phone,
		selected,
		handleOpenNotificationModal,
		hasPendingAddRequest,
	} = props;

	return (
		<TableRow
			hover
			tabIndex={-1}
			role="checkbox"
			selected={selected}
			sx={{ "&:hover": { bgcolor: alpha(colors.primary, 0.03) } }}
		>
			{/* Avatar */}
			<TableCell sx={{ pl: 2, width: 60 }}>
				<Avatar
					alt={fullName}
					src={profileImage}
					sx={{ width: 36, height: 36, fontSize: "0.9rem" }}
				>
					{fullName.charAt(0)}
				</Avatar>
			</TableCell>

			{/* Full Name */}
			<TableCell>
				<Typography
					sx={{
						fontFamily: fonts.montserrat,
						fontWeight: 700,
						fontSize: "0.82rem",
						color: colors.primary,
					}}
				>
					{fullName}
				</Typography>
			</TableCell>

			{/* Email */}
			<TableCell>
				<Typography
					sx={{
						fontFamily: fonts.montserrat,
						fontSize: "0.8rem",
						color: colors.darkGrey,
					}}
				>
					{email}
				</Typography>
			</TableCell>

			{/* Phone */}
			<TableCell>
				<Typography
					sx={{
						fontFamily: fonts.montserrat,
						fontSize: "0.8rem",
						color: colors.darkGrey,
					}}
				>
					{phone}
				</Typography>
			</TableCell>

			{/* Action */}
			<TableCell>
				<SendAddRequestBtn
					requestId={requestId ?? ""}
					onNotification={handleOpenNotificationModal}
					fullName={fullName}
					disabled={hasPendingAddRequest}
				/>
			</TableCell>
		</TableRow>
	);
};

export default SearchUserResultTable;

SearchUserResultTable.propType = {
	requestId: PropTypes.string,
	profileImage: PropTypes.string,
	fullName: PropTypes.string,
	email: PropTypes.string,
	phone: PropTypes.string,
	handleSendRequest: PropTypes.func,
	selected: PropTypes.any,
};

interface SendAddRequestBtnProps {
	requestId: string;
	onClick?: any;
	disabled?: boolean;
	onNotification: any;
	fullName: string;
}

export const theme = createTheme({
	components: {
		MuiTooltip: {
			styleOverrides: {
				tooltip: {
					fontSize: "1rem",
					color: "#fff",
					backgroundColor: "rgba(0, 0, 0, 0.87)",
				},
			},
		},
	},
});

function SendAddRequestBtn(props: SendAddRequestBtnProps) {
	const { requestId, onClick, onNotification, fullName, disabled } = props;

	const [disableButton, setDisableButton] = useState(disabled);

	const { circleId } = useParams<{ circleId: string }>();

	const currentCircleInfo = useSelector(selectedCurrentCircleInfo);

	const isLoading = useSelector(selectIsLoading);

	const dispatch = useDispatch<AppDispatch>();

	const circleDetail = currentCircleInfo.find((c) => c._id === circleId);

	const handleSend = async () => {
		if (onClick) {
			onClick();
		}

		let notificationMessage = "";

		try {
			const response = await dispatch(
				fetchLeaderSendAddRequest({
					unionId: circleDetail?._id || "",
					memberToAddId: requestId,
				})
			);

			unwrapResult(response);

			notificationMessage = `Your request to add ${fullName} has been successfully sent. You will be notified once the request is processed.`;

			setDisableButton(true);
		} catch (error: any) {
			notificationMessage = error;
		}

		if (onNotification) {
			onNotification(notificationMessage);
		}
	};

	return (
		<ThemeProvider theme={theme}>
			<Stack spacing={2} direction="row">
				{isLoading ? (
					<LoadingSpinner loading={isLoading} />
				) : (
					<Tooltip
						title={
							disableButton
								? "A request has already been sent out to this member"
								: ""
						}
						placement="top-start">
						<span>
							<SendAddRequestButton
								onClick={handleSend}
								variant="contained"
								size="small"
								disabled={disableButton || isLoading}>
								Send Request
							</SendAddRequestButton>
						</span>
					</Tooltip>
				)}
			</Stack>
		</ThemeProvider>
	);
}

SendAddRequestBtn.propType = {
	requestId: PropTypes.string,
	onClick: PropTypes.func,
	onNotification: PropTypes.any,
	fullName: PropTypes.string,
};
